import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
  isNumber,
  SelectInput,
  TextInput,
} from '../Inputs/Inputs'
import RedX from '../Graphics/RedX'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'
import {cloneDeep} from "lodash/lang.js";

function PumpState (props) {
  const {
    device, intl, report, test,
  } = props

  const stateProps = cloneDeep(props)
  delete stateProps.device
  delete stateProps.intl
  delete stateProps.report

  const {
    device: {
      configuration: {
        model,
      } = {},
    } = {},
  } = props

  let initialRelay

  if (model === 'pump2' && /^pump(Running|Stopped)State[12]$/.test(test)) {
    if (test.endsWith('1')) {
      initialRelay = 'relay1'
    } else {
      initialRelay = 'relay2'
    }
  }

  const [state, setState] = useState({
    ...stateProps,
    invalid: true,
    relay: initialRelay,
    type: 'triggerFavorite',
  })

  const formInputsAreInvalid = () => {
    const invalid = (!!report &&
        (!!report.relay1Run || !!report.relay2Run) &&
        !state.relay) ||
      !(!!state.relay &&
        isNumber(state.notificationInterval)) ||
      !state.subject

    return invalid
  }

  const updateNewRule = () => {
    const newRule = cloneDeep(state)

    delete newRule.formula
    delete newRule.invalid
    delete newRule.relay
    delete newRule.report

    AppDispatcher.dispatch({
      action: 'update-new-rule-parameters',
      ...newRule,
    })
  }

  const handleChange = (field, value, callback) => {
    let valueU = value
    if (field === 'sendOnce') {
      valueU = value === 'true'
    }

    setState((prevState) => ({
      ...prevState,
      [field]: valueU,
    }))

    if (callback) callback()
  }

  const handleChangeInterval = (field, value, callback) => {
    setState((prevState) => ({
      ...prevState,
      [field]: Number(value),
      invalid: formInputsAreInvalid(),
    }))

    if (callback) callback()
  }

  const handleChangeRelay = (field, relayName, callback) => {
    const { formula } = props

    const relayNumber = relayName === 'relay1' ? 1 : 2
    const test = `${formula}State${relayNumber}`

    setState((prevState) => ({
      ...prevState,
      invalid: formInputsAreInvalid(),
      relay: relayName,
      test,
    }))

    if (callback) callback()
  }

  const childChange = (event) => {
    switch (event.target.name) {
      case 'relay':
        handleChangeRelay(event.target.name, event.target.value)
        break

      case 'subject':
      case 'test':
      case 'method':
      case 'sendOnce':
        handleChange(event.target.name, event.target.value)
        break

      case 'notificationInterval':
        handleChangeInterval(event.target.name, event.target.value)
        break

      default:
        break
    }
  }

  useEffect(() => {
    updateNewRule()
  }, [state])

  useEffect(() => {
    updateNewRule()
  }, [])

  const {
    device: {
      physical: {
        deviceNumber = device.address_alias,
      } = {},
    } = {},
    intl: {
      formatMessage,
    },
  } = props

  const translate = (id, defaultMessage) => formatMessage({ id, defaultMessage })

  const placeholderOption = (value) => {
    if (value) {
      return []
    }

    return [
      new Object({
        name: '',
        value: '',
        selected: true,
        hidden: true,
      }),
    ]
  }

  class Option {
    constructor (id, defaultMessage, value) {
      this.name = /^Relay [1-9]$/.test(defaultMessage) ? translate(id, defaultMessage) : defaultMessage
      this.value = value
    }
  }

  const {
    notificationInterval,
    sendOnce,
    relay,
  } = state

  let relayOptions = []

  if (report && !!report.relay1Run && !!report.relay2Run) {
    const {
      physical: {
        primaryPumpLabel = 'Relay 1',
        secondaryPumpLabel = 'Relay 2',
      } = {},
    } = device

    relayOptions = [
      ...placeholderOption(relay),
      new Option('rules.pump.relay.1', primaryPumpLabel, 'relay1'),
      new Option('rules.pump.relay.2', secondaryPumpLabel, 'relay2'),
    ]
  }

  return (
    <div id='PumpState'>
      <section>
        <FormattedMessage
          id='pump.name'
          defaultMessage='Pump Name'
          description='pump name label'
          tagName='h5'
        />
        <div className={styles.groupName}>
          <TextInput
            childChange={childChange}
            disabled
            field='subject'
            value={deviceNumber}
            style={styles.groupNameInput}
          />
        </div>
        {!state.subject && (
          <RedX
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '16px',
              height: '24px',
              width: '24px',
            }}
          />
        )}
      </section>
      {(report && !!report.relay1Run && !!report.relay2Run) && (
        <section>
          <FormattedMessage
            id='pump.relay'
            defaultMessage='Relay'
            description='Relay heading'
            tagName='h5'
          />
          <div className={styles.ruleSelect}>
            <SelectInput
              value={state.relay}
              field='relay'
              childChange={childChange}
              options={relayOptions}
              style={styles.ruleSelectInput}
            />
          </div>
        </section>
      )}
      <section>
        <FormattedMessage
          id='rules.snooze.name'
          defaultMessage='Snooze Alerts'
          description='Snooze Alerts heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={notificationInterval}
            field='notificationInterval'
            childChange={childChange}
            options={[
              {
                value: 0,
                name: formatMessage({
                  id: 'rules.reminders.never',
                  defaultMessage: 'None',
                  description: 'None option',
                }),
              },
              {
                value: 900000,
                name: formatMessage({
                  id: 'rules.reminders.every15Min',
                  defaultMessage: '15 minutes',
                  description: '15 minutes option',
                }),
              },
              {
                value: 3600000,
                name: formatMessage({
                  id: 'rules.reminders.every1Hrs',
                  defaultMessage: '1 hour',
                  description: '1 hour option',
                }),
              },
              {
                value: 14400000,
                name: formatMessage({
                  id: 'rules.reminders.every4Hrs',
                  defaultMessage: '4 hours',
                  description: '4 hours option',
                }),
              },
              {
                value: 64800000,
                name: formatMessage({
                  id: 'rules.reminders.every18Hrs',
                  defaultMessage: '18 hours',
                  description: '18 hours option',
                }),
              },
              {
                value: 86400000,
                name: formatMessage({
                  id: 'rules.reminders.every24Hrs',
                  defaultMessage: '24 hours',
                  description: '24 hours option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
      <section>
        <FormattedMessage
          id='rules.reminders.name'
          defaultMessage='Send Reminders'
          description='Send Reminders heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect}>
          <SelectInput
            value={sendOnce}
            field='sendOnce'
            childChange={childChange}
            options={[
              {
                value: true,
                name: formatMessage({
                  id: 'rules.sendOnce.off',
                  defaultMessage: 'Off',
                  description: 'Off option',
                }),
              },
              {
                value: false,
                name: formatMessage({
                  id: 'rules.sendOnce.on',
                  defaultMessage: 'On',
                  description: 'On option',
                }),
              },
            ]}
            style={styles.ruleSelectInput}
          />
        </div>
      </section>
    </div>
  )
}

PumpState.propTypes = {
  rule: PropTypes.object,
  device: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
}

export default injectIntl(PumpState)
